import React from 'react';
import { MapIcon, CheckCircleIcon, ClockIcon, AlertTriangleIcon, FileTextIcon, ScaleIcon, PlusIcon, UploadIcon, BarChart3Icon, ArrowRightIcon, CalendarIcon } from 'lucide-react';
import { useApp } from '../context/AppContext';
interface KPICardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  onClick: () => void;
}
function KPICard({
  title,
  value,
  icon,
  color,
  onClick
}: KPICardProps) {
  return <button onClick={onClick} className="bg-white rounded-xl border border-gray-200 p-5 text-left hover:shadow-md hover:border-gray-300 transition-all group" data-id="element-87">
      <div className="flex items-start justify-between" data-id="element-88">
        <div data-id="element-89">
          <p className="text-sm text-gray-600 mb-1" data-id="element-90">{title}</p>
          <p className="text-3xl font-bold text-gray-900" data-id="element-91">{value}</p>
        </div>
        <div className={`p-3 rounded-xl ${color}`} data-id="element-92">{icon}</div>
      </div>
      <div className="mt-3 flex items-center text-sm text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-93">
        <span data-id="element-94">View details</span>
        <ArrowRightIcon className="w-4 h-4 ml-1" data-id="element-95" />
      </div>
    </button>;
}
export function Dashboard() {
  const {
    state,
    dispatch,
    navigate,
    canCreate
  } = useApp();
  const {
    landRecords,
    notifications,
    language
  } = state;
  // Calculate KPIs
  const totalRecords = landRecords.length;
  const activeRecords = landRecords.filter(r => r.status === 'Active').length;
  const pendingVerification = landRecords.filter(r => r.verification === 'Pending').length;
  const today = new Date();
  const thirtyDaysFromNow = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
  const khajanaDue = landRecords.filter(r => {
    if (!r.khajna?.dueDate || r.khajna.upToDate) return false;
    const dueDate = new Date(r.khajna.dueDate);
    return dueDate <= thirtyDaysFromNow;
  }).length;
  const mutationFollowUp = landRecords.filter(r => {
    if (!r.mutation?.followUpDue) return false;
    const dueDate = new Date(r.mutation.followUpDue);
    return dueDate <= thirtyDaysFromNow;
  }).length;
  const ongoingLitigation = landRecords.filter(r => r.litigation.some(l => l.status === 'Ongoing')).length;
  // Due soon items
  const dueSoonItems = landRecords.flatMap(r => {
    const items = [];
    if (r.khajna && !r.khajna.upToDate && r.khajna.dueDate) {
      const dueDate = new Date(r.khajna.dueDate);
      if (dueDate <= thirtyDaysFromNow) {
        const daysUntil = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        items.push({
          type: 'Khajna',
          landId: r.landId,
          landName: r.landName,
          dueDate: r.khajna.dueDate,
          daysUntil,
          recordId: r.id
        });
      }
    }
    if (r.mutation?.followUpDue) {
      const dueDate = new Date(r.mutation.followUpDue);
      if (dueDate <= thirtyDaysFromNow) {
        const daysUntil = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        items.push({
          type: 'Mutation',
          landId: r.landId,
          landName: r.landName,
          dueDate: r.mutation.followUpDue,
          daysUntil,
          recordId: r.id
        });
      }
    }
    return items;
  }).sort((a, b) => a.daysUntil - b.daysUntil).slice(0, 5);
  const handleKPIClick = (filter: string) => {
    switch (filter) {
      case 'total':
        dispatch({
          type: 'CLEAR_FILTERS'
        });
        break;
      case 'active':
        dispatch({
          type: 'SET_FILTERS',
          payload: {
            status: 'Active'
          }
        });
        break;
      case 'pending':
        dispatch({
          type: 'SET_FILTERS',
          payload: {
            verification: 'Pending'
          }
        });
        break;
      case 'khajna':
        dispatch({
          type: 'SET_FILTERS',
          payload: {
            searchQuery: 'khajna due'
          }
        });
        break;
      case 'mutation':
        dispatch({
          type: 'SET_FILTERS',
          payload: {
            searchQuery: 'mutation'
          }
        });
        break;
      case 'litigation':
        dispatch({
          type: 'SET_FILTERS',
          payload: {
            searchQuery: 'litigation'
          }
        });
        break;
    }
    navigate('land-records');
  };
  return <div className="p-4 lg:p-6 space-y-6" data-id="element-96">
      {/* Page Header */}
      <div data-id="element-97">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-98">
          {language === 'en' ? 'Dashboard' : 'ড্যাশবোর্ড'}
        </h1>
        <p className="text-gray-600 mt-1" data-id="element-99">
          {language === 'en' ? 'Overview of land records and pending actions' : 'ভূমি রেকর্ড এবং মুলতুবি কার্যক্রমের সংক্ষিপ্ত বিবরণ'}
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4" data-id="element-100">
        <KPICard title={language === 'en' ? 'Total Records' : 'মোট রেকর্ড'} value={totalRecords} icon={<MapIcon className="w-6 h-6 text-blue-600" data-id="element-102" />} color="bg-blue-50" onClick={() => handleKPIClick('total')} data-id="element-101" />
        <KPICard title={language === 'en' ? 'Active' : 'সক্রিয়'} value={activeRecords} icon={<CheckCircleIcon className="w-6 h-6 text-green-600" data-id="element-104" />} color="bg-green-50" onClick={() => handleKPIClick('active')} data-id="element-103" />
        <KPICard title={language === 'en' ? 'Pending Verification' : 'যাচাই মুলতুবি'} value={pendingVerification} icon={<ClockIcon className="w-6 h-6 text-amber-600" data-id="element-106" />} color="bg-amber-50" onClick={() => handleKPIClick('pending')} data-id="element-105" />
        <KPICard title={language === 'en' ? 'Khajna Due (30d)' : 'খাজনা বকেয়া (৩০দিন)'} value={khajanaDue} icon={<AlertTriangleIcon className="w-6 h-6 text-red-600" data-id="element-108" />} color="bg-red-50" onClick={() => handleKPIClick('khajna')} data-id="element-107" />
        <KPICard title={language === 'en' ? 'Mutation Follow-up' : 'মিউটেশন ফলোআপ'} value={mutationFollowUp} icon={<FileTextIcon className="w-6 h-6 text-purple-600" data-id="element-110" />} color="bg-purple-50" onClick={() => handleKPIClick('mutation')} data-id="element-109" />
        <KPICard title={language === 'en' ? 'Ongoing Litigation' : 'চলমান মামলা'} value={ongoingLitigation} icon={<ScaleIcon className="w-6 h-6 text-orange-600" data-id="element-112" />} color="bg-orange-50" onClick={() => handleKPIClick('litigation')} data-id="element-111" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6" data-id="element-113">
        {/* Due Soon List */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-114">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between" data-id="element-115">
            <h2 className="font-semibold text-gray-900" data-id="element-116">
              {language === 'en' ? 'Due Soon' : 'শীঘ্রই বকেয়া'}
            </h2>
            <button onClick={() => navigate('notifications')} className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-117">
              {language === 'en' ? 'View all' : 'সব দেখুন'}
            </button>
          </div>

          {dueSoonItems.length > 0 ? <div className="divide-y divide-gray-100" data-id="element-118">
              {dueSoonItems.map((item, index) => <button key={`${item.recordId}-${item.type}-${index}`} onClick={() => {
            dispatch({
              type: 'SET_SELECTED_RECORD',
              payload: item.recordId
            });
            navigate('land-record-details', item.recordId);
          }} className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors text-left" data-id="element-119">
                  <div className="flex items-center gap-4" data-id="element-120">
                    <div className={`p-2 rounded-lg ${item.type === 'Khajna' ? 'bg-red-50' : 'bg-purple-50'}`} data-id="element-121">
                      {item.type === 'Khajna' ? <AlertTriangleIcon className="w-5 h-5 text-red-600" data-id="element-122" /> : <FileTextIcon className="w-5 h-5 text-purple-600" data-id="element-123" />}
                    </div>
                    <div data-id="element-124">
                      <p className="font-medium text-gray-900" data-id="element-125">
                        {item.landName}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-126">
                        {item.landId} • {item.type}
                      </p>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-127">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${item.daysUntil <= 3 ? 'bg-red-100 text-red-700' : item.daysUntil <= 7 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'}`} data-id="element-128">
                      <CalendarIcon className="w-3 h-3" data-id="element-129" />
                      {item.daysUntil <= 0 ? language === 'en' ? 'Overdue' : 'বকেয়া' : `${item.daysUntil} ${language === 'en' ? 'days' : 'দিন'}`}
                    </span>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-130">{item.dueDate}</p>
                  </div>
                </button>)}
            </div> : <div className="px-5 py-12 text-center text-gray-500" data-id="element-131">
              <CheckCircleIcon className="w-12 h-12 mx-auto mb-3 text-green-500" data-id="element-132" />
              <p data-id="element-133">
                {language === 'en' ? 'No upcoming dues' : 'কোনো আসন্ন বকেয়া নেই'}
              </p>
            </div>}
        </div>

        {/* Quick Actions */}
        {canCreate() && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-134">
            <div className="px-5 py-4 border-b border-gray-100" data-id="element-135">
              <h2 className="font-semibold text-gray-900" data-id="element-136">
                {language === 'en' ? 'Quick Actions' : 'দ্রুত কার্যক্রম'}
              </h2>
            </div>
            <div className="p-4 space-y-3" data-id="element-137">
              <button onClick={() => navigate('create-record')} className="w-full flex items-center gap-3 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-138">
                <PlusIcon className="w-5 h-5" data-id="element-139" />
                <span className="font-medium" data-id="element-140">
                  {language === 'en' ? 'Create Land Record' : 'ভূমি রেকর্ড তৈরি'}
                </span>
              </button>
              <button onClick={() => navigate('documents')} className="w-full flex items-center gap-3 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors" data-id="element-141">
                <UploadIcon className="w-5 h-5" data-id="element-142" />
                <span className="font-medium" data-id="element-143">
                  {language === 'en' ? 'Upload Document' : 'দলিল আপলোড'}
                </span>
              </button>
              <button onClick={() => navigate('reports')} className="w-full flex items-center gap-3 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors" data-id="element-144">
                <BarChart3Icon className="w-5 h-5" data-id="element-145" />
                <span className="font-medium" data-id="element-146">
                  {language === 'en' ? 'Generate Report' : 'রিপোর্ট তৈরি'}
                </span>
              </button>
            </div>
          </div>}
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-147">
        <div className="px-5 py-4 border-b border-gray-100" data-id="element-148">
          <h2 className="font-semibold text-gray-900" data-id="element-149">
            {language === 'en' ? 'Recent Activity' : 'সাম্প্রতিক কার্যক্রম'}
          </h2>
        </div>
        <div className="divide-y divide-gray-100" data-id="element-150">
          {landRecords.sort((a, b) => new Date(b.lastUpdatedDate).getTime() - new Date(a.lastUpdatedDate).getTime()).slice(0, 5).map(record => <button key={record.id} onClick={() => {
          dispatch({
            type: 'SET_SELECTED_RECORD',
            payload: record.id
          });
          navigate('land-record-details', record.id);
        }} className="w-full px-5 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors text-left" data-id="element-151">
                <div className="flex items-center gap-3" data-id="element-152">
                  <div className="w-2 h-2 rounded-full bg-blue-500" data-id="element-153" />
                  <div data-id="element-154">
                    <p className="font-medium text-gray-900" data-id="element-155">
                      {record.landName}
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-156">
                      {language === 'en' ? 'Updated by' : 'আপডেট করেছেন'}{' '}
                      {record.lastUpdatedBy}
                    </p>
                  </div>
                </div>
                <span className="text-sm text-gray-500" data-id="element-157">
                  {record.lastUpdatedDate}
                </span>
              </button>)}
        </div>
      </div>
    </div>;
}